package dev.danko.roblox_craft_ai.uikit.components

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.ObjectFit
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.attrsModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.text.SpanText
import dev.danko.roblox_craft_ai.shared.model.GenerationQuality
import dev.danko.roblox_craft_ai.shared.model.MeshViews
import dev.danko.roblox_craft_ai.uikit.icons.AppIcon
import dev.danko.roblox_craft_ai.uikit.icons.Icon
import dev.danko.roblox_craft_ai.uikit.i18n.strings
import dev.danko.roblox_craft_ai.uikit.theme.LocalAppTheme
import org.jetbrains.compose.web.css.AlignItems
import org.jetbrains.compose.web.css.DisplayStyle
import org.jetbrains.compose.web.css.FlexWrap
import org.jetbrains.compose.web.css.JustifyContent
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.Position
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Button
import org.jetbrains.compose.web.dom.Img

/**
 * Front / back / left / right pictures of one object for the 3D tab. An empty slot opens the file picker for that
 * view; pictures dropped or pasted into the composer fill the slots (a model sheet is cut by the caller).
 */
@Composable
fun ViewSlots(
    views: Map<String, String>,
    splitting: Boolean,
    onPick: (view: String) -> Unit,
    onRemove: (view: String) -> Unit,
    onSwapSides: () -> Unit
) {
    val s = strings().promptBox
    val deleteLabel = strings().common.delete
    val palette = LocalAppTheme.current.colors
    val names = mapOf(MeshViews.FRONT to s.viewFront, MeshViews.BACK to s.viewBack, MeshViews.LEFT to s.viewLeft, MeshViews.RIGHT to s.viewRight)

    Column(Modifier.fillMaxWidth().gap(8.px).padding(leftRight = 8.px)) {
        SpanText(s.viewsTitle, Modifier.fontSize(12.px).fontWeight(FontWeight.SemiBold).color(palette.textSecondary))
        Row(Modifier.gap(10.px).flexWrap(FlexWrap.Wrap), verticalAlignment = Alignment.Top) {
            MeshViews.ALL.forEach { view ->
                val picture = views[view]
                Column(Modifier.gap(4.px), horizontalAlignment = Alignment.CenterHorizontally) {
                    Box(
                        Modifier
                            .size(72.px)
                            .borderRadius(10.px)
                            .border(1.px, if (picture == null) LineStyle.Dashed else LineStyle.Solid, palette.borderStrong)
                            .backgroundColor(palette.background)
                            .position(Position.Relative)
                            .cursor(Cursor.Pointer)
                            .onClick { if (!splitting) onPick(view) }
                            .attrsModifier { attr("role", "button"); attr("aria-label", names.getValue(view)); attr("title", names.getValue(view)) },
                        contentAlignment = Alignment.Center
                    ) {
                        if (picture == null) {
                            Icon(AppIcon.PLUS, size = 18)
                        } else {
                            Img(src = picture, attrs = Modifier.fillMaxWidth().fillMaxHeight().objectFit(ObjectFit.Contain).borderRadius(10.px).toAttrs())
                            Button(
                                Modifier
                                    .position(Position.Absolute)
                                    .top((-6).px)
                                    .right((-6).px)
                                    .size(22.px)
                                    .padding(0.px)
                                    .borderRadius(50.percent)
                                    .border(1.px, LineStyle.Solid, palette.borderStrong)
                                    .backgroundColor(palette.surfaceElevated)
                                    .color(palette.textPrimary)
                                    .cursor(Cursor.Pointer)
                                    .display(DisplayStyle.Flex)
                                    .alignItems(AlignItems.Center)
                                    .justifyContent(JustifyContent.Center)
                                    .toAttrs {
                                        attr("type", "button")
                                        attr("aria-label", deleteLabel)
                                        onClick { event -> event.stopPropagation(); onRemove(view) }
                                    }
                            ) { Icon(AppIcon.CLOSE, size = 12) }
                        }
                    }
                    SpanText(names.getValue(view), Modifier.fontSize(12.px).color(palette.textSecondary))
                }
            }
        }
        if (splitting) {
            SpanText(s.viewsSplitting, Modifier.fontSize(12.px).color(palette.statusProcessing))
        } else {
            SpanText(s.viewsHint, Modifier.fontSize(12.px).color(palette.textSecondary))
        }
        if (views.containsKey(MeshViews.LEFT) || views.containsKey(MeshViews.RIGHT)) {
            AppButton(text = s.viewsSwapSides, variant = AppButtonVariant.OUTLINE, size = AppButtonSize.SM, onClick = onSwapSides)
        }
        if (views.size > 1 && QualityPrefs.mesh == GenerationQuality.HIGH) {
            SpanText(s.viewsHighOnlyFront, Modifier.fontSize(12.px).color(palette.statusPending))
            AppButton(text = s.viewsSwitchToMax, variant = AppButtonVariant.SECONDARY, size = AppButtonSize.SM, onClick = { QualityPrefs.chooseMesh(GenerationQuality.MAX) })
        }
    }
}
